<template>
  <div class="data-table">
    <!-- filters (optional) -->
    <b-row v-if="showFilters">
      <b-col md="12" class="my-1">
        <b-card class="text-left mb-3 rounded-0" no-body>
          <b-card-header class="rounded-0">
            <b-row>
              <b-col class="d-flex align-items-center">
                <div class="d-inline-flex">
                  <span class="survey-results__icon-wrapper">
                    <i
                      class="mdi mdi-filter-outline phairify-color-grey"
                      aria-hidden="true"
                    />
                  </span>
                  <h2 slot="header" class="my-0">
                    <strong
                      class="ml-1 phairify-color-navy phairify-heading-h2"
                    >
                      {{ filterTitle }}
                    </strong>
                  </h2>
                </div>
              </b-col>
              <b-col
                cols="6"
                lg="4"
                class="d-flex align-items-center justify-content-end"
              >
                <b-button
                  variant="link"
                  class="my-0 py-0 phairify-reset-button text-uppercase"
                  @click="resetFilters()"
                >
                  Reset
                </b-button>
              </b-col>
            </b-row>
          </b-card-header>
          <slot name="filters" />
        </b-card>
      </b-col>
    </b-row>

    <b-alert
      show
      class="selected-items"
      variant="success"
      v-if="rowSelection && selectedItems.length"
    >
      {{ selectedItems.length }}
      {{ selectedItems.length == 1 ? 'Item' : 'Items' }}
      Selected
      <div class="float-right">
        <slot name="multi" v-bind="selectedItems" />
      </div>
    </b-alert>

    <b-table
      show-empty
      :no-local-sorting="noLocalSorting"
      :items="items ? items : itemProvider"
      ref="table"
      :fields="fields"
      :selectable="rowSelection"
      selectedVariant="success"
      @row-selected="rowSelected"
      :current-page="context.currentPage"
      :per-page="context.perPage"
      :sort-by.sync="context.sortBy"
      :sort-desc.sync="context.sortDesc"
      :class="{
        'admin-table' : adminTable,
        tableClass
      }"
      :busy="busy"
      :empty-html="emptyHtml"
      :borderless="borderLess"
    >
      <!-- re-create templates defined in parent b-table component -->
      <template v-for="col in getCustomColumns()" :slot="col" slot-scope="data">
        <slot :name="col" v-bind="data" />
      </template>
    </b-table>

    <!-- pager (optional) -->
    <b-row v-if="showPager">
      <b-col md="12" class="my-1">
        <b-input-group class="mt-3">
          <template v-slot:append>
            <b-pagination
              :total-rows="context.totalRows"
              :per-page="context.perPage"
              v-model="context.currentPage"
              class="my-0 mr-5"
            />
          </template>
          <div width="100px">
            <b-form-input
              type="number"
              v-model="pageToGo"
              @keyup.enter="goToPage"
              :min="1"
              :max="maxPaginationPage"
            />
          </div>
          <b-button variant="v3_teal_outline" class="ml-3" @click="goToPage">
            Go to page
          </b-button>
        </b-input-group>
      </b-col>
    </b-row>
  </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
/*
 * This component wraps b-table and b-pagination to provide a generic data table with sortable columns,
 * plus optional filters and pagination.
 *
 * Custom column formatting is done just as it would be with the <b-table> component, except that all
 * fields that need custom formatting must be flagged in the 'fields' data array - e.g.:
 *
 * <DataTable ...>
 *     <template slot="myField" slot-scope="data">
 *         {{ data.item.myField.myAttr1 + data.item.myField.myAttr2}}
 *     </template>
 * </DataTable>
 *
 * data() {
 *     ...
 *     fields: [
 *         { key: 'myField', sortable: true, custom: true },
 *     ],
 */

import debounce from 'lodash/debounce';

export default {
  name: 'dataTable',
  props: {
    rowSelection: Boolean,
    showFilters: Boolean,
    showPager: Boolean,
    getItems: [Function, Array],
    items: Array,
    filters: Object,
    filterTitle: {
      type: String,
      default: 'Filter',
    },
    fields: Array,
    noLocalSorting: {
      type: Boolean,
      default: true,
    },
    tableClass: {
      type: String,
      default: '',
    },
    emptyHtml: {
      type: [String, undefined],
      default: undefined,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    adminTable: {
      type: Boolean,
      default: true,
    },
    borderLess: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedItems: [],
      context: {
        totalRows: null,
        perPage: 10,
        currentPage: 1,
        sortBy: 'id',
        sortDesc: false,
      },
      pageToGo: 1,
    };
  },
  watch: {
    items: {
      immediate: true,
      handler: function (newVal) {
        this.context.totalRows = newVal ? newVal.length : 0;
      },
    },
  },
  computed: {
    maxPaginationPage: function () {
      return Math.ceil(this.context.totalRows / this.context.perPage);
    },
  },
  methods: {
    rowSelected(items) {
      this.selectedItems = items;
    },
    getCustomColumns() {
      return this.fields.filter((f) => f.custom).map((cf) => cf.key);
    },
    itemProvider(ctx) {
      return this.getItems(ctx).then((items) => {
        this.context = ctx;
        return items;
      });
    },
    resetFilters() {
      Object.values(this.filters).forEach((f) => (f.value = null));
      this.refresh(); // do it now (this will result in two refreshes with the watch below)
    },
    refresh() {
      this.$refs.table.refresh();
    },
    goToPage: function () {
      this.$set(this.context, 'currentPage', this.pageToGo);
    },
  },
  mounted() {
    this.debouncedRefresh = debounce(this.refresh, 1000);
    this.$watch(
      'filters',
      function () {
        this.debouncedRefresh();
      },
      {
        deep: true,
      }
    );
  },
};
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
@import '../../sass/variables_mixins.scss';

.phairify-reset-button {
  @include phairify_heading_bold_14;
  font-weight: 400;
  color: $phairify_secondary_color_grey;
  &.collapsed > .when-opened,
  &:not(.collapsed) > .when-closed {
    display: none;
  }
  &:focus {
    box-shadow: none;
  }
}
</style>
